import React, { useState, useEffect } from "react";
import {
  Drawer,
  Card,
  Text,
  Button,
  Stack,
  Group,
  Avatar,
  ScrollArea,
  Badge,
  Divider,
  Paper,
  Box,
  ActionIcon,
  Tooltip,
  Select,
  ThemeIcon,
} from "@mantine/core";
import {
  collection,
  collectionGroup,
  updateDoc,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase/FirebaseCredential";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import {
  IconCalendar,
  IconUsers,
  IconRefresh,
  IconUserCircle,
  IconCreditCard,
  IconBuilding,
  IconCircleCheck,
  IconCircleOff,
  IconClock,
  IconMinusVertical,
  IconActivity,
  IconSettings,
  IconLogout,
  IconPackage,
  IconBook,
  IconChevronRight,
} from "@tabler/icons-react";

import { toast } from "react-toastify";
import { logError } from "../ErrorLogService/ErrorLoggingService";
import BenchLoader from "../UIComponent/BenchLoader/BenchLoader";

import { Link, useNavigate } from "react-router-dom";
import { logout } from "../../features/authSlice";
import { useAppDispatch } from "../../hooks/hooks";
import { showToast } from "../Utils/toastHelper";
import { ROUTES } from "../../routes/routesPaths";
import { DefaultSystemPermission } from "../PermissionComponent/DefaultSystemPermission";

interface AdminData {
  first_name?: string;
  last_name?: string;
  email?: string;
  mobile_number?: string;
  designation?: string;
  monthly_ctc?: string;
  checkin_device_id?: string;
  type?: string;
  status?: string;
  uid?: string;
}

interface SubscriptionData {
  plan_name: string;
  plan_id: string | number;
  subscription_start_date: { seconds: number };
  subscription_end_date: { seconds: number };
  allowed_staff_limit: string | number;
  features: { [key: string]: boolean };
  company_id?: string;
  status?: string;
}

interface ProfileSubscriptionDrawerProps {
  opened: boolean;
  onClose: () => void;
  companyId?: string;
  roleDetails?: {
    company_admin?: boolean;
  };
}

const ProfileDrawer = ({
  opened,
  onClose,
  companyId,
  roleDetails,
}: ProfileSubscriptionDrawerProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [adminData, setAdminData] = useState<AdminData>({});
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData[]>(
    []
  );
  const [refreshing, setRefreshing] = useState(false);
  const [status, setStatus] = useState<string>("inactive");
  const [statusUpdating, setStatusUpdating] = useState(false);

  const auth = getAuth();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const canUpdate = DefaultSystemPermission("subscription", "update");

  const statusOptions = [
    {
      label: "Active",
      value: "active",
      icon: IconCircleCheck,
      color: "green",
    },
    {
      label: "Inactive",
      value: "inactive",
      icon: IconCircleOff,
      color: "gray",
    },
    {
      label: "Busy",
      value: "busy",
      icon: IconClock,
      color: "orange",
    },
    {
      label: "DND",
      value: "dnd",
      icon: IconMinusVertical,
      color: "red",
    },
  ];

  // Helper function to format null/undefined values
  const formatValue = (value?: string) => {
    return value && value.trim() !== "" ? value : " ";
  };

  // Format date function
  const formatDate = (timestamp: { seconds: number }) => {
    if (!timestamp?.seconds) return "N/A";
    return new Date(timestamp.seconds * 1000).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Calculate days remaining
  const getDaysRemaining = (endDate: { seconds: number }) => {
    if (!endDate?.seconds) return 0;
    const now = new Date().getTime();
    const end = new Date(endDate.seconds * 1000).getTime();
    const diffTime = end - now;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Handle status change
  async function handleStatusChange(newStatus: string) {
    if (!companyId || !auth.currentUser?.uid) return;

    setStatusUpdating(true);
    try {
      const benchprofileDocRef = doc(
        db,
        "benchprofile",
        auth.currentUser.uid,
        "status",
        companyId
      );

      await updateDoc(benchprofileDocRef, {
        activity_status: newStatus,
        updated_at: serverTimestamp(),
      });

      setStatus(newStatus);
      toast.success(
        `Status updated to ${statusOptions.find((opt) => opt.value === newStatus)?.label
        }`
      );
      console.log(`Status updated to ${newStatus}`);
    } catch (error) {
      console.error("Error updating status:", error);
      logError(error, "handleStatusChange");
      toast.error("Failed to update status");
    } finally {
      setStatusUpdating(false);
    }
  }

  // Fetch current status
  const fetchCurrentStatus = async (uid: string) => {
    if (!companyId) return;

    try {
      const statusDocRef = doc(db, "benchprofile", uid, "status", companyId);
      const statusDoc = await getDoc(statusDocRef);

      if (statusDoc.exists()) {
        const statusData = statusDoc.data();
        setStatus(statusData.activity_status || "active");
      }
    } catch (error) {
      console.error("Error fetching status:", error);
      logError(error, "fetchCurrentStatus");
    }
  };

  useEffect(() => {
    if (opened) {
      const auth = getAuth();
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        if (user) {
          await Promise.all([
            fetchAdminData(user.uid),
            fetchCurrentStatus(user.uid),
          ]);
        }
      });

      if (companyId) {
        fetchCompanySubscriptionDetails();
      }

      return () => unsubscribe();
    }
  }, [opened, companyId, roleDetails]);

  const fetchAdminData = async (uid: string) => {
    try {
      const adminQuery = query(
        collection(db, "admin"),
        where("uid", "==", uid)
      );
      const querySnapshot = await getDocs(adminQuery);

      if (!querySnapshot.empty) {
        const data = querySnapshot.docs[0].data() as AdminData;
        setAdminData(data);
      }
    } catch (error) {
      logError(error, "fetchAdminData");
    }
  };

  const fetchCompanySubscriptionDetails = async () => {
    if (!companyId) return;

    try {
      setIsLoading(true);
      const collectionRef = collection(
        db,
        `companyprofile/${companyId}/benchsubscription`
      );
      const q = query(
        collectionRef,

        where("status", "==", "Active")
      );
      const querySnapshot = await getDocs(q);

      const settings = querySnapshot.docs?.map(
        (doc) => doc.data() as SubscriptionData
      );
      console.log("Fetched subscription settings:", settings);
      if (settings.length > 0) {
        setSubscriptionData((prevData: SubscriptionData[]) =>
          JSON.stringify(prevData) !== JSON.stringify(settings)
            ? settings
            : prevData
        );
      } else {
        setSubscriptionData([]);
      }
    } catch (error) {
      console.error("Error fetching subscription details:", error);
      logError(error, "fetchCompanySubscriptionDetails");
      toast.error("Failed to load subscription details");
    } finally {
      setIsLoading(false);
    }
  };

  const handleProfileOverviewClick = () => {
    navigate(ROUTES.PROFILE);
    onClose();
  };

  const handleManageSubscription = () => {
    navigate("/managesubscription");
    onClose();
  };

  const handleUserGuideClick = () => {
    navigate(ROUTES.USER_GUIDE);
    onClose();
  };

  const handleLogout = async () => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user || !companyId) return;

    try {
      const statusDocRef = doc(
        db,
        "benchprofile",
        user.uid,
        "status",
        companyId
      );
      const statusDocSnap = await getDoc(statusDocRef);
      if (statusDocSnap.exists()) {
        await updateDoc(statusDocRef, {
          activity_status: "inactive",
          updated_at: new Date(),
        });
      }
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      await signOut(auth);
      dispatch(logout());
      navigate("/");
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchCompanySubscriptionDetails();
    setRefreshing(false);
    showToast.success("dataRefreshed");
  };

  const handleClose = () => {
    onClose();
  };

  const getStatusColor = (statusValue: string) => {
    const statusOption = statusOptions.find((opt) => opt.value === statusValue);
    return statusOption?.color || "gray";
  };

  const getStatusIcon = (statusValue: string) => {
    const statusOption = statusOptions.find((opt) => opt.value === statusValue);
    const IconComponent = statusOption?.icon || IconCircleCheck;
    return <IconComponent size={14} />;
  };

  // Background pattern styles for header
  const headerPatternStyles = {
    background: `
      linear-gradient(135deg, #f2354b 0%, #e8213a 100%),
      url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='3'/%3E%3Ccircle cx='10' cy='10' r='2'/%3E%3Ccircle cx='50' cy='10' r='2'/%3E%3Ccircle cx='10' cy='50' r='2'/%3E%3Ccircle cx='50' cy='50' r='2'/%3E%3Ccircle cx='30' cy='10' r='1.5'/%3E%3Ccircle cx='30' cy='50' r='1.5'/%3E%3Ccircle cx='10' cy='30' r='1.5'/%3E%3Ccircle cx='50' cy='30' r='1.5'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")
    `,
    backgroundSize: "60px 60px",
    backgroundRepeat: "repeat",
    position: "relative" as const,
    overflow: "hidden" as const,
  };
  const handlePurchase = () => {
    navigate(ROUTES.NEW_SUBSCRIPTION_PAGE);
    onClose();
  };
  return (
    <>
      <Drawer
        opened={opened}
        onClose={handleClose}
        title={
          <Group spacing="sm" align="center">
            <Avatar size="md" radius="xl">
              <IconUserCircle size={20} />
            </Avatar>
            <Box>
              <Text size="lg" weight={600} color="white">
                {formatValue(adminData?.first_name)}{" "}
                {formatValue(adminData?.last_name)}
              </Text>
              <Text size="sm" color="rgba(255,255,255,0.8)">
                {adminData?.designation || adminData?.email || "User Profile"}
              </Text>
            </Box>
          </Group>
        }
        padding="md"
        size="md"
        position="right"
        overlayProps={{ opacity: 0.5, blur: 2 }}
        styles={{
          header: {
            ...headerPatternStyles,
            borderBottom: "none",
            // Add a subtle bottom border with pattern overlay
            "::after": {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "1px",
              background: "rgba(255, 255, 255, 0.2)",
            },
          },
          title: {
            color: "white",
            fontWeight: 600,
            width: "100%",
            position: "relative",
            zIndex: 1,
          },
          body: {
            backgroundColor: "#f8f9fa",
          },
          content: {
            backgroundColor: "#f8f9fa",
          },
        }}
      >
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            height: "calc(100vh - 120px)",
          }}
        >
          <Stack spacing="md" mt={"lg"}>
            {/* Status Section */}
            <Card
              padding="md"
              radius="md"
              withBorder
              style={{
                backgroundColor: "white",
                borderColor: "#e9ecef",
              }}
            >
              <Group spacing="sm" mb="md" align="center">
                <ThemeIcon size="sm" radius="md">
                  <IconActivity size={16} />
                </ThemeIcon>
                <Text weight={600} size="sm" color="#495057">
                  Activity Status
                </Text>
              </Group>

              <Select
                placeholder="Select your status"
                data={statusOptions.map((option) => ({
                  value: option.value,
                  label: option.label,
                }))}
                value={status}
                onChange={(value) => value && handleStatusChange(value)}
                disabled={statusUpdating}
                icon={getStatusIcon(status)}
                rightSection={
                  statusUpdating ? (
                    <div style={{ width: 14, height: 14 }}>
                      <BenchLoader isLoading={true} />
                    </div>
                  ) : null
                }
                styles={{
                  input: {
                    backgroundColor: "white",
                    border: "1px solid #ced4da",
                    color: getStatusColor(status),
                    fontWeight: 500,
                  },
                }}
                withinPortal
              />
            </Card>

            {/* Subscriptions Section */}
            <Card
              padding="md"
              radius="md"
              withBorder
              style={{
                backgroundColor: "white",
                borderColor: "#e9ecef",
              }}
            >
              <Group position="apart" mb="md">
                <Group spacing="sm" align="center">
                  <ThemeIcon size="sm" radius="md">
                    <IconCreditCard size={16} />
                  </ThemeIcon>
                  <Text weight={600} size="sm" color="#495057">
                    Subscriptions
                  </Text>
                </Group>
                <Tooltip label="Refresh Data">
                  <ActionIcon
                    variant="subtle"
                    color="blue"
                    size="sm"
                    onClick={handleRefresh}
                    loading={refreshing}
                  >
                    <IconRefresh size={14} />
                  </ActionIcon>
                </Tooltip>
              </Group>

              {subscriptionData.length > 0 ? (
                <Stack spacing="sm">
                  {subscriptionData.map((sub, index) => {
                    const daysRemaining = getDaysRemaining(
                      sub.subscription_end_date
                    );
                    const isExpiringSoon = daysRemaining <= 30;

                    return (
                      <Paper
                        key={index}
                        p="md"
                        radius="md"
                        style={{
                          backgroundColor: isExpiringSoon
                            ? "#fff5f5"
                            : "#f0f9ff",
                          border: `1px solid ${isExpiringSoon ? "#fed7d7" : "#bfdbfe"
                            }`,
                        }}
                      >
                        <Group position="apart" mb="sm" align="flex-start">
                          <Box>
                            <Text weight={600} size="sm" color="#374151">
                              {sub.plan_name}
                            </Text>
                            <Text size="xs" color="#6b7280">
                              Plan ID: {sub.plan_id}
                            </Text>
                          </Box>
                          <Badge
                            color={isExpiringSoon ? "red" : "green"}
                            size="sm"
                            variant="filled"
                          >
                            {daysRemaining > 0
                              ? `${daysRemaining} days`
                              : "Expired"}
                          </Badge>
                        </Group>

                        <Stack spacing="xs" mb="md">
                          <Group spacing="sm">
                            <IconCalendar size={12} color="#6b7280" />
                            <Text size="xs" color="#6b7280">
                              {formatDate(sub.subscription_start_date)} to{" "}
                              {formatDate(sub.subscription_end_date)}
                            </Text>
                          </Group>

                          <Group spacing="sm">
                            <IconUsers size={12} color="#6b7280" />
                            <Text size="xs" color="#6b7280">
                              {sub.allowed_staff_limit || 0} users allowed
                            </Text>
                          </Group>
                        </Stack>
                        {/*
                        <Button
                          variant="light"
                          color={isExpiringSoon ? "red" : "blue"}
                          size="xs"
                          fullWidth
                          leftIcon={<IconPackage size={14} />}
                          onClick={handleManageSubscription}
                        >
                          Manage Subscription
                        </Button> */}
                        {canUpdate && (
                          <Button
                            variant="light"
                            color={isExpiringSoon ? "red" : "blue"}
                            size="xs"
                            fullWidth
                            leftIcon={<IconPackage size={14} />}
                            onClick={handlePurchase}
                          >
                            Purchase
                          </Button>
                        )}
                      </Paper>
                    );
                  })}
                </Stack>
              ) : (
                <Paper
                  p="md"
                  radius="md"
                  style={{
                    backgroundColor: "#f8f9fa",
                    border: "1px solid #e9ecef",
                  }}
                >
                  <Stack align="center" spacing="xs">
                    <ThemeIcon size="lg" color="gray" variant="light">
                      <IconBuilding size={20} />
                    </ThemeIcon>
                    <Text size="sm" color="#6c757d" ta="center">
                      No active subscriptions
                    </Text>
                  </Stack>
                </Paper>
              )}
            </Card>

            {/* User Guide Section */}
            <Card
              padding="md"
              radius="md"
              withBorder
              onClick={handleUserGuideClick}
              style={{
                backgroundColor: "white",
                borderColor: "#e9ecef",
                cursor: "pointer",
              }}
            >
              <Group position="apart">
                <Group spacing="sm" align="center">
                  <ThemeIcon size="sm" radius="md">
                    <IconBook size={16} />
                  </ThemeIcon>
                  <Text weight={600} size="sm" color="#495057">
                    User Guide
                  </Text>
                </Group>
                <IconChevronRight size={16} color="#adb5bd" />
              </Group>
            </Card>
          </Stack>

          {/* Bottom Action Buttons */}
          <Box style={{ marginTop: "auto", paddingTop: "1rem" }}>
            <Divider mb="md" />
            <Group position="apart" spacing="xs">
              <Button
                size="sm"
                // leftIcon={<IconSettings size={16} />}
                onClick={handleProfileOverviewClick}
                style={{ flex: 1 }}
              >
                My Profile
              </Button>

              <Button
                variant="outline"
                size="sm"
                leftIcon={<IconLogout size={16} />}
                onClick={handleLogout}
                style={{ flex: 1 }}
              >
                Logout
              </Button>
            </Group>
          </Box>
        </Box>
      </Drawer>
      <BenchLoader isLoading={isLoading || statusUpdating} />
    </>
  );
};

export default ProfileDrawer;
